import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { navItems } from '../../_nav';

export const STORAGE_ALLOWED_ACTIONS = 'allowedActions';

// First in-app path used as home when the matching UI action is allowed.
const ACTION_HOME: { action: string; url: string }[] = [
  { action: 'ui.product.nav.dashboard_list', url: '/products/dashboard-list' },
  { action: 'ui.document.nav.list', url: '/documents/list' },
  { action: 'ui.supplier.nav.list', url: '/suppliers/list' },
  { action: 'ui.report.nav.list', url: '/reportings/list' },
  { action: 'ui.budget.nav', url: '/budget' },
  { action: 'ui.chat.nav', url: '/chat/main' },
  { action: 'ui.tag.nav.settings', url: '/tags/list' },
  { action: 'ui.ops.nav.users', url: '/users/list' },
  { action: 'ui.pos.nav.settings', url: '/pos-sync' },
];
@Injectable({
  providedIn: 'root',
})
export class TenantEntitlementService {
  // NavComponent rebuilds the sidebar when GET /tenant/init writes a new allowlist.
  private allowedActionsChanged = new Subject<void>();
  readonly allowedActionsChanged$ = this.allowedActionsChanged.asObservable();

  // False until storeAllowedActions (or a cached restricted list) resolves the plan.
  // Missing localStorage must not mean "all allowed" or gated UI flashes then hides.
  private entitlementsResolved = false;

  constructor() {
    // Restricted tenants already have a cached list — apply it immediately (no wait for init).
    if (this.readStoredAllowlist() !== null) {
      this.entitlementsResolved = true;
    }
  }

  // Emit once when storeAllowedActions has resolved the plan (or immediately if already resolved).
  // TagService and similar wait on this so they do not call off-plan APIs before init finishes.
  whenEntitlementsResolved(): Observable<void> {
    if (this.entitlementsResolved) {
      return of(undefined);
    }
    return this.allowedActionsChanged$.pipe(
      filter(() => this.entitlementsResolved),
      take(1),
      map(() => undefined)
    );
  }

  // True after storeAllowedActions (or a cached restricted list) has resolved the plan.
  // NavComponent uses this to keep a sidebar spinner until menu items can be filtered.
  areEntitlementsResolved(): boolean {
    return this.entitlementsResolved;
  }

  // Persist allowlist from GET /tenant/init. null means every action is allowed.
  // AuthConfigurationService.setTenantConfig calls this.
  storeAllowedActions(allowedActions: string[] | null | undefined): void {
    if (allowedActions == null) {
      localStorage.removeItem(STORAGE_ALLOWED_ACTIONS);
    } else {
      localStorage.setItem(STORAGE_ALLOWED_ACTIONS, JSON.stringify(allowedActions));
    }
    this.entitlementsResolved = true;
    this.allowedActionsChanged.next();
  }

  // Drop cached allowlist on logout so the next tenant cannot inherit it.
  // AuthConfigurationService.cleanLocalStorage calls this.
  clearAllowedActions(): void {
    localStorage.removeItem(STORAGE_ALLOWED_ACTIONS);
    this.entitlementsResolved = false;
    this.allowedActionsChanged.next();
  }

  // True when the tenant may use this action. Until entitlements are resolved, returns false
  // so gated UI stays hidden by default. After resolve: null allowlist = all actions.
  // Nav, settings menus, screens, and post-login redirect call this.
  isActionAllowed(action: string): boolean {
    if (!this.entitlementsResolved) {
      return false;
    }
    const allowed = this.readStoredAllowlist();
    if (allowed == null) {
      return true;
    }
    return allowed.indexOf(action) !== -1;
  }

  // First allowed sidebar URL, used as home when products dashboard is off-plan.
  // Nav brand links and unauthorized login redirect call this.
  homeUrl(): string {
    for (const item of navItems) {
      const action = item.entitlementAction;
      const url = typeof item.url === 'string' ? item.url : null;
      if (!url) {
        continue;
      }
      if (!action || this.isActionAllowed(action)) {
        return url;
      }
    }
    for (let i = 0; i < ACTION_HOME.length; i++) {
      const entry = ACTION_HOME[i];
      if (this.isActionAllowed(entry.action)) {
        return entry.url;
      }
    }
    return '/unauthorized';
  }

  // True when the current router URL belongs to an action that is off-plan.
  // NavComponent uses this to bounce to homeUrl.
  isUrlAllowed(url: string): boolean {
    if (!this.entitlementsResolved) {
      // Avoid bouncing home before init finishes; redirectIfCurrentRouteOffPlan runs again after.
      return true;
    }
    const path = (url || '').split('?')[0];
    if (path.indexOf('/products/dashboard-list') === 0) {
      return this.isActionAllowed('ui.product.nav.dashboard_list');
    }
    if (path.indexOf('/products/dashboard-procurement') === 0) {
      return this.isActionAllowed('ui.product.nav.dashboard_procurement');
    }
    // Product type list/details: reuse the matching sidebar item's entitlementAction from _nav.
    const listMatch = path.match(/^\/products\/list\/([^/]+)/);
    if (listMatch) {
      const listUrl = '/products/list/' + listMatch[1];
      const nav = navItems.find((item) => item.url === listUrl);
      return nav?.entitlementAction
        ? this.isActionAllowed(nav.entitlementAction)
        : false;
    }
    const detailsMatch = path.match(/^\/products\/details\/([^/]+)/);
    if (detailsMatch) {
      const listUrl = '/products/list/' + detailsMatch[1];
      const nav = navItems.find((item) => item.url === listUrl);
      if (!nav?.entitlementAction || !this.isActionAllowed(nav.entitlementAction)) {
        return false;
      }
      return this.isActionAllowed('ui.product.screen.details');
    }
    if (path.indexOf('/products/import') === 0) {
      return this.isActionAllowed('ui.product.screen.import');
    }
    if (path.indexOf('/products') === 0) {
      // Non-list product routes: allow if any product-type nav item is on-plan.
      return navItems.some(
        (item) =>
          typeof item.url === 'string' &&
          item.url.indexOf('/products/list/') === 0 &&
          !!item.entitlementAction &&
          this.isActionAllowed(item.entitlementAction)
      );
    }
    if (path.indexOf('/documents') === 0) {
      return this.isActionAllowed('ui.document.screen.list');
    }
    if (path.indexOf('/suppliers') === 0) {
      return this.isActionAllowed('ui.supplier.screen.list');
    }
    if (path.indexOf('/tags') === 0) {
      return this.isActionAllowed('ui.tag.screen.list');
    }
    if (path.indexOf('/reportings') === 0) {
      return this.isActionAllowed('ui.report.screen.list');
    }
    if (path.indexOf('/budget') === 0) {
      return this.isActionAllowed('ui.budget.screen');
    }
    if (path.indexOf('/chat/admin') === 0) {
      return this.isActionAllowed('ui.chat.screen.admin');
    }
    if (path.indexOf('/chat') === 0) {
      return this.isActionAllowed('ui.chat.screen.main');
    }
    if (path.indexOf('/users') === 0) {
      return this.isActionAllowed('ui.ops.screen.users');
    }
    if (path.indexOf('/tenant-config') === 0) {
      return this.isActionAllowed('ui.ops.screen.tenant_config');
    }
    if (path.indexOf('/scheduled-tasks') === 0) {
      return this.isActionAllowed('ui.ops.screen.scheduled_tasks');
    }
    if (path.indexOf('/pos-sync') === 0) {
      return this.isActionAllowed('ui.pos.screen');
    }
    return true;
  }

  // Sidebar items whose entitlementAction is allowed. Empty until entitlements resolve.
  // NavComponent assigns the result to navItems.
  allowedNavItems() {
    if (!this.entitlementsResolved) {
      return [];
    }
    return navItems.filter((item) => {
      if (!item.entitlementAction) {
        return true;
      }
      return this.isActionAllowed(item.entitlementAction);
    });
  }

  // Parse localStorage JSON. null = unrestricted (only meaningful after entitlementsResolved).
  // Empty array / missing key / invalid JSON => null.
  private readStoredAllowlist(): string[] | null {
    const raw = localStorage.getItem(STORAGE_ALLOWED_ACTIONS);
    if (raw == null || raw === '') {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
